import { useCallback, useEffect, useRef, useState } from "react";
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { BookOpen, KeyRound, LogOut, Menu, RefreshCw, Users } from "lucide-react";

import { useAuth } from "@/auth/AuthProvider";
import { FleetAttention } from "@/components/FleetAttention";
import { Logo } from "@/components/Logo";
import { Pill, type PillTone } from "@/components/Pill";

interface HealthState {
  tone: PillTone;
  label: string;
  pulse: boolean;
  title?: string;
}

export function Layout() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [health, setHealth] = useState<HealthState>({
    tone: "neutral",
    label: "checking",
    pulse: true,
  });

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await fetch("/readyz");
        if (cancelled) return;
        if (res.ok) {
          setHealth({ tone: "ok", label: "ready", pulse: false });
        } else {
          setHealth({
            tone: "warn",
            label: "warming up",
            pulse: true,
            title: `HTTP ${res.status}`,
          });
        }
      } catch (err) {
        if (cancelled) return;
        setHealth({
          tone: "danger",
          label: "down",
          pulse: false,
          title: err instanceof Error ? err.message : String(err),
        });
      }
    };
    void tick();
    const id = window.setInterval(tick, 8_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  return (
    <div className="app">
      <header className="topbar">
        <Link to="/" className="topbar__brand" aria-label="Home">
          <Logo size={22} />
          <div className="topbar__wordmark">
            Back<span className="accent">Stage</span>
          </div>
        </Link>

        <div className="topbar__actions">
          {/* Fleet-wide attention chips ("N need attention" /
              "N self-healing") — visible from every page so an unhealthy fleet is
              impossible to miss without digging into Events. */}
          <FleetAttention />

          <Pill tone={health.tone} pulse={health.pulse} title={health.title}>
            {health.label}
          </Pill>

          <RefreshControl backendDown={health.tone === "danger"} />

          {user && (
            <UserMenu
              email={user.email}
              onLogout={() =>
                void logout().then(() => navigate("/login", { replace: true }))
              }
            />
          )}
        </div>
      </header>
      <main className="shell shell--single">
        <Outlet />
      </main>
    </div>
  );
}

/**
 * Top-bar "refresh now" button.
 *
 * Forces every currently-mounted TanStack Query to re-run, so the user
 * doesn't have to wait out the per-screen polling cadence (8s for the
 * tree, 15s for pool error banners, etc.) when they want fresh state
 * after a controller-side change.
 *
 * The icon spins for at least 500ms even if the cache responds
 * instantly, so the click always feels acknowledged. Honors
 * `prefers-reduced-motion` via the `.spin` rule in styles.css.
 */
function RefreshControl({ backendDown }: { backendDown: boolean }) {
  const qc = useQueryClient();
  const [spinning, setSpinning] = useState(false);
  const [pollSeconds, setPollSeconds] = useState(30);
  const inFlightRef = useRef(false);
  const MIN_SPIN_MS = 900;
  const POLL_OPTIONS = [15, 30, 60, 120] as const;

  const runRefresh = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setSpinning(true);
    const spinStartAt = performance.now();
    try {
      await qc.refetchQueries({ type: "active" });
      const elapsed = performance.now() - spinStartAt;
      if (elapsed < MIN_SPIN_MS) {
        await new Promise<void>((resolve) =>
          setTimeout(resolve, MIN_SPIN_MS - elapsed),
        );
      }
    } finally {
      setSpinning(false);
      inFlightRef.current = false;
    }
  }, [qc]);

  useEffect(() => {
    const id = window.setInterval(() => {
      // Don't hammer the timer-driven auto-refresh when there's nobody
      // (or nobody watching) to serve it: skip while the backend is
      // unreachable (reuse the /readyz health signal) or the tab is
      // hidden. The manual refresh button and the 8s /readyz poll still
      // run, so recovery is picked up promptly.
      if (backendDown || document.hidden) return;
      void runRefresh();
    }, pollSeconds * 1000);
    return () => window.clearInterval(id);
  }, [pollSeconds, runRefresh, backendDown]);

  return (
    <div className="topbar__refresh-control">
      <button
        type="button"
        className="btn btn--ghost topbar__refresh-btn"
        onClick={() => void runRefresh()}
        disabled={spinning}
        aria-label="Refresh now"
        title={spinning ? "Refreshing…" : `Refresh now (auto ${pollSeconds}s)`}
      >
        <RefreshCw
          className={
            spinning
              ? "topbar__refresh-icon topbar__refresh-icon--spin"
              : "topbar__refresh-icon"
          }
          aria-hidden
        />
      </button>
      <div className="topbar__poll-group" role="group" aria-label="Poll interval">
        {POLL_OPTIONS.map((sec) => (
          <button
            key={sec}
            type="button"
            className={`topbar__poll-btn ${pollSeconds === sec ? "is-active" : ""}`}
            onClick={() => setPollSeconds(sec)}
            aria-pressed={pollSeconds === sec}
            title={`Auto refresh every ${sec}s`}
          >
            {sec}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Right-hand burger menu in the top bar. Replaces the previous
 * "email pill + logout" pair with a single button that opens a dropdown
 * containing every account-scoped action: Account (password change),
 * Teams (member admin), and Logout.
 *
 * Dismissal rules:
 *  - click outside the popover  -> close
 *  - press Escape               -> close (and refocus the trigger)
 *  - navigate to a new route    -> close (so the popover doesn't linger
 *                                  over the destination page)
 */
function UserMenu({
  email,
  onLogout,
}: {
  email: string;
  onLogout: () => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (ev: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(ev.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="user-menu" ref={wrapRef}>
      <button
        type="button"
        ref={triggerRef}
        className="btn btn--ghost btn--sm user-menu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        title={`Signed in as ${email}`}
        onClick={() => setOpen((v) => !v)}
      >
        <Menu />
      </button>
      {open && (
        <div className="user-menu__pop" role="menu">
          <div className="user-menu__head">
            <div className="user-menu__head-label">Signed in as</div>
            <div className="user-menu__head-email" title={email}>
              {email}
            </div>
          </div>
          <Link
            to="/reference/phases"
            role="menuitem"
            className="user-menu__item"
            onClick={() => setOpen(false)}
          >
            <BookOpen />
            <span>Documentation</span>
          </Link>
          <Link
            to="/account"
            role="menuitem"
            className="user-menu__item"
            onClick={() => setOpen(false)}
          >
            <KeyRound />
            <span>Account</span>
          </Link>
          <Link
            to="/teams"
            role="menuitem"
            className="user-menu__item"
            onClick={() => setOpen(false)}
          >
            <Users />
            <span>Teams</span>
          </Link>
          <div className="user-menu__divider" />
          <button
            type="button"
            role="menuitem"
            className="user-menu__item user-menu__item--danger"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
          >
            <LogOut />
            <span>Logout</span>
          </button>
        </div>
      )}
    </div>
  );
}

